import { useState } from 'react'
import { useConnection } from '@solana/wallet-adapter-react'
import { useDagProgram } from './useDagProgram'
import { useAccount } from './useAccount'
import { api } from '~/services/api'
import { CreateProposalRequest, CreateProposalResponse } from '~/types/api'

interface TaskCreationData {
  title: string
  description: string
  bountyBps: number
  gameId: string
  tokenAddress: string
}

interface TaskCreationResult {
  txSignature: string
  proposal: CreateProposalResponse['proposal']
}

interface TransactionResult {
  txSignature: string
  proposalPda: string
  title: string
  description: string
}

export const useTaskCreation = () => {
  const { connection } = useConnection()
  const { createProposal } = useDagProgram()
  const { account, isAuthenticated, checkGitHubConnection } = useAccount()
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isCreatingTask, setIsCreatingTask] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [transactionResult, setTransactionResult] = useState<TransactionResult | null>(null)

  const createTask = async (data: TaskCreationData): Promise<TaskCreationResult> => {
    if (!account?.address) {
      throw new Error('Wallet not connected')
    }

    if (!isAuthenticated) {
      throw new Error('Please sign in first')
    }

    setIsLoading(true)
    setError(null)
    setTransactionResult(null)
    setIsConfirming(false)
    setIsCreatingTask(false)

    try {
      // Check GitHub connection
      const hasGitHub = await checkGitHubConnection()
      if (!hasGitHub) {
        throw new Error('GitHub account must be connected to create a task')
      }

      // Step 1: Send transaction and get signature
      console.log('Creating proposal on-chain...')
      const { proposalPda, txSignature } = await createProposal({
        title: data.title,
        description: data.description,
        tokenAddress: data.tokenAddress,
        bountyBps: data.bountyBps
      })

      console.log('Transaction sent:', {
        proposalPda: proposalPda.toBase58(),
        txSignature
      })

      const txResult: TransactionResult = {
        txSignature,
        proposalPda: proposalPda.toBase58(),
        title: data.title,
        description: data.description
      }
      setTransactionResult(txResult)

      // Step 2: Create task through API
      setIsCreatingTask(true)
      const taskResponse = await createTaskWithRetry(
        txResult.proposalPda,
        data.title,
        data.description
      )
      setIsCreatingTask(false)

      console.log('Task created through API:', taskResponse)

      return {
        txSignature,
        proposal: taskResponse.proposal,
      }
    } catch (err) {
      console.error('Task creation failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create task'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
      setIsConfirming(false)
      setIsCreatingTask(false)
    }
  }

  const createTaskWithRetry = async (
    proposalPda: string,
    title: string,
    description: string
  ): Promise<CreateProposalResponse> => {
    const taskRequest: CreateProposalRequest = {
      proposalPda,
      title,
      description
    }

    console.log('Creating task through API...')
    return await api.proposals.create(taskRequest)
  }

  const retryTaskCreation = async (): Promise<CreateProposalResponse> => {
    if (!transactionResult) {
      throw new Error('No transaction result available for retry')
    }

    setIsCreatingTask(true)
    setError(null)

    try {
      const taskResponse = await createTaskWithRetry(
        transactionResult.proposalPda,
        transactionResult.title,
        transactionResult.description
      )
      console.log('Task created successfully on retry:', taskResponse)
      return taskResponse
    } catch (err) {
      console.error('Task creation retry failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create task'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsCreatingTask(false)
    }
  }

  return {
    createTask,
    retryTaskCreation,
    isLoading,
    isConfirming,
    isCreatingTask,
    error,
    transactionResult,
    clearError: () => setError(null),
  }
} 